import React, { useEffect, useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  analyticsService,
  ConsommationEnergie as Donnees,
  SerieEnergie,
} from "../services/analytics.service";
import { getErrorMessage } from "../utils/error";
import { masquerAvantEntree } from "../utils/periodeLocataire";
import "../styles/ConsommationEnergie.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const MOIS = ["jan", "fév", "mar", "avr", "mai", "jui", "jul", "aoû", "sep", "oct", "nov", "déc"];
const SEMESTRES = ["1er sem.", "2nd sem."];

/** Somme deux valeurs dont chacune peut être absente.
 *  `null + null` reste `null` — un semestre sans facture est un TROU, pas un
 *  zéro. `null + 62` vaut 62 : une seule facture suffit à renseigner le
 *  semestre. */
const somme = (a: number | null, b: number | null): number | null =>
  a == null && b == null ? null : (a ?? 0) + (b ?? 0);

/** Une teinte par énergie, deux tons par teinte. L'année précédente en clair
 *  DERRIÈRE, l'année en cours en foncé DEVANT : la comparaison se lit d'un coup
 *  d'œil, sans légende à décoder. */
const TEINTES: Record<string, { clair: string; fonce: string }> = {
  electricite: { clair: "#F7D9A8", fonce: "#E08A17" },
  gaz: { clair: "#BFD6EC", fonce: "#2E6FB7" },
  eau: { clair: "#B7E1E3", fonce: "#159699" },
};

type Mesure = "euro" | "conso";

interface Props {
  colocId: string;
  /** Entrée dans les lieux, sur l'espace locataire. Les mois d'avant sont
   *  laissés vides dans la série de l'année en cours : ce ne sont pas ses
   *  dépenses. Absente côté propriétaire, qui voit l'année entière. */
  depuis?: Date | null;
  /** Espace locataire d'une colocation « consommations seulement » : kWh et m³,
   *  sans bascule vers les euros (le serveur ne les envoie d'ailleurs pas). */
  sansMontants?: boolean;
}

const ConsommationEnergie: React.FC<Props> = ({ colocId, depuis = null, sansMontants = false }) => {
  const [donnees, setDonnees] = useState<Donnees | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  // Une seule bascule pour les trois graphiques : les comparer dans deux unités
  // différentes n'aurait aucun sens, et trois interrupteurs pour un seul geste
  // en font trois à retrouver.
  const [mesureChoisie, setMesure] = useState<Mesure>("euro");
  const mesure: Mesure = sansMontants ? "conso" : mesureChoisie;

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    setErreur(null);
    analyticsService
      .getConsommationEnergie(colocId)
      .then((d) => vivant && setDonnees(d))
      .catch((e) => vivant && setErreur(getErrorMessage(e, "Consommations indisponibles")))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
  }, [colocId]);

  const uniteDe = (serie: SerieEnergie) =>
    mesure === "euro" ? "€" : serie.unite === "m3" ? "m³" : serie.unite || "";

  const graphique = (serie: SerieEnergie) => {
    if (!donnees) return null;
    const teinte = TEINTES[serie.cle] || TEINTES.gaz;
    const valeur = (m: (typeof serie.mois)[0], precedent: boolean) =>
      mesure === "euro"
        ? precedent
          ? m.montantPrecedent
          : m.montant
        : precedent
        ? m.consommationPrecedente
        : m.consommation;

    // Un fournisseur semestriel ne remplit pas douze cases : on obtient des
    // barres isolées, et surtout la comparaison se DÉSALIGNE dès que le mois de
    // facturation dérive — l'eau est passée de mai à avril entre 2025 et 2026,
    // si bien que la barre de 2026 se retrouvait À CÔTÉ de celle de 2025 au lieu
    // d'être devant. Or la superposition est toute la lecture du graphique.
    const semestriel = serie.periodicite === "semestrielle";
    const labels = semestriel ? SEMESTRES : MOIS;
    const serieDe = (precedent: boolean) => {
      let brut = serie.mois.map((m) => valeur(m, precedent));
      // L'année PRÉCÉDENTE reste entière : c'est le fond de comparaison, et
      // elle est déjà signalée comme telle par sa teinte claire. Seule la série
      // en cours — celle qu'on lit comme « la sienne » — est coupée. Le masque
      // s'applique AVANT le repliement, sinon un semestre entier disparaîtrait
      // pour un seul mois d'avant l'entrée.
      if (!precedent) brut = masquerAvantEntree(brut, donnees.annee, depuis);
      if (!semestriel) return brut;
      return [
        brut.slice(0, 6).reduce(somme, null),
        brut.slice(6, 12).reduce(somme, null),
      ];
    };

    const data = {
      labels,
      datasets: [
        {
          label: String(donnees.anneePrecedente),
          data: serieDe(true),
          backgroundColor: teinte.clair,
          // `grouped: false` superpose au lieu de juxtaposer. La barre claire est
          // plus large et dessinée en premier : elle sert de fond de comparaison.
          grouped: false,
          barPercentage: 0.92,
          categoryPercentage: 0.8,
          order: 2,
        },
        {
          label: String(donnees.annee),
          data: serieDe(false),
          backgroundColor: teinte.fonce,
          grouped: false,
          barPercentage: 0.5,
          categoryPercentage: 0.8,
          order: 1,
        },
      ],
    };

    const suffixe = uniteDe(serie);
    return (
      <Bar
        data={data as never}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          // `skipNull` évite de tracer une barre à ras du sol là où la donnée
          // est absente : un mois sans relevé n'est pas un mois sans usage.
          plugins: {
            legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
              callbacks: {
                label: (c) =>
                  `${c.dataset.label} : ${
                    c.parsed.y == null
                      ? "—"
                      : c.parsed.y.toLocaleString("fr-FR", { maximumFractionDigits: 0 })
                  } ${suffixe}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (v) => `${Number(v).toLocaleString("fr-FR")} ${suffixe}`,
                font: { size: 10 },
              },
            },
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
          },
        }}
      />
    );
  };

  // Le bloc s'appelle « Consommations » : une énergie qu'on ne sait pas mesurer
  // n'y a pas sa place, dans AUCUNE des deux unités. Son coût reste visible dans
  // le graphique des dépenses juste en dessous, donc rien n'est perdu.
  const energies = useMemo(
    () => (donnees?.energies ?? []).filter((s) => s.consommationFiable),
    [donnees],
  );
  // Écartées ET dignes d'être signalées. On ne le dit QUE s'il y a quelque chose
  // à faire : soit des factures à qualifier dans l'écran Factures, soit un relevé
  // réel dans le passé — auquel cas la série a dérivé et le fournisseur peut la
  // remettre d'aplomb. Une énergie qui n'a JAMAIS été relevée (compteur d'eau non
  // télérelevé) produirait un message permanent qu'on ne peut pas faire
  // disparaître : au bout de deux semaines ce n'est plus une information, c'est
  // du mobilier.
  const ecartees = useMemo(
    () =>
      (donnees?.energies ?? []).filter(
        (s) => !s.consommationFiable && (s.nonQualifiees > 0 || s.dernierReleve),
      ),
    [donnees],
  );

  if (chargement) {
    return (
      <div className="conso-energie">
        <p className="conso-etat">Chargement des consommations…</p>
      </div>
    );
  }
  if (erreur) {
    return (
      <div className="conso-energie">
        <p className="conso-etat conso-erreur">{erreur}</p>
      </div>
    );
  }
  // Rien à montrer plutôt qu'un cadre vide : une colocation sans facture
  // d'énergie n'a pas besoin d'un bloc qui l'annonce.
  if (energies.length === 0 && ecartees.length === 0) return null;

  return (
    <div className="conso-energie">
      <div className="conso-entete">
        <h3>
          Consommations <span className="conso-annees">{donnees!.annee} vs {donnees!.anneePrecedente}</span>
        </h3>
        {!sansMontants && (
        <div className="conso-bascule" role="group" aria-label="Unité d'affichage">
          <button
            type="button"
            className={mesure === "euro" ? "actif" : ""}
            onClick={() => setMesure("euro")}
            aria-pressed={mesure === "euro"}
          >
            €
          </button>
          <button
            type="button"
            className={mesure === "conso" ? "actif" : ""}
            onClick={() => setMesure("conso")}
            aria-pressed={mesure === "conso"}
          >
            kWh / m³
          </button>
        </div>
        )}
      </div>

      {ecartees.length > 0 && (
        <p className="conso-ecartees">
          {ecartees.map((s) => s.libelle).join(", ")} —{" "}
          {ecartees.some((s) => s.nonQualifiees > 0) ? (
            <>
              nature du relevé non renseignée sur{" "}
              {ecartees.reduce((n, s) => n + s.nonQualifiees, 0)} facture(s).
              Ouvrez-les depuis l’écran Factures et indiquez si la consommation est
              relevée ou estimée.
            </>
          ) : (
            <>
              {/* La date est forcément là : sans facture à qualifier, une série
                  n'est écartée-et-signalée que si elle a DÉJÀ été relevée. */}
              consommations estimées, dernier relevé réel le{" "}
              {new Date(ecartees[0].dernierReleve!).toLocaleDateString("fr-FR")}.
              Un historique bâti sur des estimations ne révèle ni fuite ni
              changement d’usage : demandez un relevé à votre fournisseur.
            </>
          )}
        </p>
      )}

      <div className="conso-grille">
        {energies.map((serie) => (
          <figure key={serie.cle} className="conso-carte">
            <figcaption>
              {serie.libelle}
              <span className="conso-unite">{uniteDe(serie)}</span>
            </figcaption>
            <div className="conso-toile">{graphique(serie)}</div>
          </figure>
        ))}
      </div>
    </div>
  );
};

export default ConsommationEnergie;
